import { vars } from '@etg/wings';
import { useDirection, useProperty, useSiteContext } from '@eti/providers';
import type { BestPricePerDay } from '@eti/schema-types';
import { css } from '@eti/styles';
import type { Locale } from 'date-fns';
import { startOfMonth } from 'date-fns';
import { type MouseEvent, useEffect, useState } from 'react';
import { DayPicker, type DayEventHandler } from 'react-day-picker';
import 'react-day-picker/src/style.css';
import { useFormContext } from 'react-hook-form';
import type { FormBound } from '../../../../common/search-form-panel/SearchFormNewContainer';
import { tripTypes } from '../../../../constants/tripTypesConstants';
import {
  MULTI_BOUND,
  RETURN_DATE,
  SINGLE_BOUND,
  type DEPARTURE_DATE,
} from '../../constants/formFields';
import {
  calculateDayRange,
  getOneYearFromToday,
  isDisabled,
  isMasked,
  isSelected,
} from '../../utils/calendarDateUtils';
import { formatWeekdayName, getLocale } from '../../utils/calendarLocales';
import { getFirstAndLastSelectableDateButtons } from '../../utils/getFirstAndLastSelectableDateButtons';
import CalendarFooter from './CalendarFooter';
import { calendarStyles } from './calendarStyles';
import ChangeMonthButton from './ChangeMonthButton';
import CustomDayWithPriceIndicator from './CustomDayWithPriceIndicator';

const calendarContainerStyle = css`
  background-color: ${vars.colors.surface.primary};
  border: 1px solid ${vars.colors.inputs.border};
  box-shadow:
    0 10px 20px rgb(0 0 0 / 4%),
    0 2px 6px rgb(0 0 0 / 4%),
    0 0 1px rgb(0 0 0 / 4%);
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  padding: 0.5rem 0.25rem 1rem;
`;

export interface CalendarNewProps {
  bestPricesPerDay?: BestPricePerDay[];
  boundIndex?: number;
  range?: { departureDate?: Date; returnDate?: Date };
  currentField?: typeof DEPARTURE_DATE | typeof RETURN_DATE | null;
  languageCode?: string;
  loading?: boolean;
  daysFromTodayForValidDepartureDate?: number;
  id?: string;
  previousBoundDepartureDate?: Date;
  selectedTripType?: string;
  shouldAutoFocusDay?: boolean;
  onDepartureNewDateChange?: (day: Date) => void;
  onReturnNewDateChange?: (day: Date) => void;
  onDepartureDateChange?: (day: string) => void;
  onReturnDateChange?: (day: string) => void;
}

const CalendarNew = ({
  bestPricesPerDay,
  boundIndex,
  range = {},
  currentField,
  daysFromTodayForValidDepartureDate,
  id,
  loading = false,
  previousBoundDepartureDate,
  selectedTripType,
  shouldAutoFocusDay = false,
  onDepartureDateChange,
  onReturnDateChange,
  onDepartureNewDateChange,
  onReturnNewDateChange,
}: CalendarNewProps) => {
  const { direction } = useDirection();
  const { getValues, trigger: validateForm } = useFormContext();
  const { language } = useSiteContext();
  const { p } = useProperty();

  const isPriceCalendarEnabled = p('IbeClient.Price.Calendar.Enabled');

  const [locale, setLocale] = useState<Locale>();

  const isValidDate = (value: unknown): value is Date => {
    return value instanceof Date && !Number.isNaN(value.getTime());
  };

  const getDefaultMonth = () => {
    const [singleBound, multiBound]: [FormBound, FormBound[]] = getValues([
      SINGLE_BOUND,
      MULTI_BOUND,
    ]);

    if (selectedTripType === tripTypes.MULTI_STOP) {
      if (!boundIndex) {
        return new Date();
      }
      const departureDate = multiBound.at(boundIndex)?.departureDate;
      if (isValidDate(departureDate)) {
        return departureDate;
      }

      const lastValidDate = multiBound
        .filter((formBound) => isValidDate(formBound.departureDate))
        .at(-1)?.departureDate;

      return new Date(lastValidDate ?? new Date());
    }

    if (!currentField) {
      return new Date();
    }

    const fieldValue = singleBound[currentField];

    if (isValidDate(fieldValue)) {
      return fieldValue;
    }

    if (
      currentField === RETURN_DATE &&
      !isValidDate(singleBound.returnDate) &&
      isValidDate(singleBound.departureDate)
    ) {
      return singleBound.departureDate;
    }

    return new Date();
  };

  useEffect(() => {
    const loadLocal = async () => {
      if (language?.locale) {
        const tempLocale = await getLocale(language.locale);
        setLocale(tempLocale);
      }
    };
    loadLocal();
  }, [language?.locale]);

  useEffect(() => {
    if (!currentField || !shouldAutoFocusDay) {
      return;
    }

    const dateButtons = getFirstAndLastSelectableDateButtons(range);

    dateButtons[currentField]?.focus();
  }, [currentField, range, shouldAutoFocusDay]);

  const handleDayClick: DayEventHandler<MouseEvent> = (day, { disabled }) => {
    if (!disabled) {
      validateForm();
      calculateDayRange(
        currentField,
        range.departureDate,
        range.returnDate,
        day,
        onDepartureDateChange || onDepartureNewDateChange,
        onReturnDateChange || onReturnNewDateChange,
      );
    }
  };

  return (
    <div className={calendarContainerStyle} id={id}>
      <DayPicker
        classNames={calendarStyles}
        components={{
          NextMonthButton: (props) => <ChangeMonthButton {...props} direction="next" />,
          PreviousMonthButton: (props) => <ChangeMonthButton {...props} direction="previous" />,
          ...(isPriceCalendarEnabled
            ? {
                DayButton: ({ day, modifiers, ...buttonProps }) => (
                  <CustomDayWithPriceIndicator
                    date={day.date}
                    loading={loading}
                    modifiers={modifiers}
                    priceCalendarData={bestPricesPerDay ?? undefined}
                    {...buttonProps}
                  />
                ),
              }
            : {}),
        }}
        defaultMonth={getDefaultMonth()}
        dir={direction}
        endMonth={getOneYearFromToday()}
        formatters={{ formatWeekdayName }}
        locale={locale}
        modifiers={{
          selected: (day) => isSelected(day, range.departureDate, range.returnDate),
          disabled: (day) =>
            isDisabled(
              day,
              daysFromTodayForValidDepartureDate,
              previousBoundDepartureDate,
              selectedTripType,
            ),
          masked: (day) => isMasked(day, selectedTripType, range.departureDate, range.returnDate),
        }}
        modifiersClassNames={{ masked: 'rdp-masked' }}
        modifiersStyles={{
          disabled: { opacity: 1 },
          today: { color: vars.colors.text.primary },
        }}
        onDayClick={handleDayClick}
        startMonth={startOfMonth(new Date())}
      />
      {selectedTripType === tripTypes.RETURN && (range.departureDate || range.returnDate) && (
        <CalendarFooter {...range} />
      )}
    </div>
  );
};

export default CalendarNew;
